import { get } from "lodash";
import {
  Modal,
  Flex,
  Button,
  Small,
  Icon,
  H3,
  Tabs,
  Box,
} from "orcs-design-system";
import PropTypes from "prop-types";
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import styled from "styled-components";

import icon from "src/config/icons";
import { reportError } from "src/services/errorReporting";
import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import UnsplashTab from "src/components/ImagePicker/tabs/UnsplashTab";
import ColorPickerTab from "src/components/ImagePicker/tabs/ColorPickerTab";
import CompanyImagesTab from "src/components/ImagePicker/tabs/CompanyImagesTab";

import ImageCropTool from "src/components/ImagePicker/components/ImageCropTool";
import { getImageUrl } from "src/components/BannerImagePicker/BannerImagePicker.util";
import { useOrgBranding } from "src/hooks/orgConfig";
import { sizing, copywriting } from "./BannerImagePicker.config";

import { IMAGE_SOURCE_TYPES, getLayoutConfig, CROP_DATA_FIELD } from "./consts";

const StyledModal = styled(Modal)`
  #modal-container > div {
    border-bottom: none !important;
  }
`;
const BannerImagePicker = ({
  isVisible,
  onUpdate,
  onHideModal,
  resourceTrackingType = EVENT_TRACKING.TEAM_UPDATED,
  allowedSources = [
    IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
    IMAGE_SOURCE_TYPES.STOCK_IMAGES,
    IMAGE_SOURCE_TYPES.COLOR_PICKER,
  ],
  initialValue = null,
  defaultBackgroundItem,
  layoutMode = "background",
  layoutConfig = null,
}) => {
  const config = layoutConfig || getLayoutConfig(layoutMode);

  const searchTermRef = useRef();
  const [activeTab, setActiveTab] = useState(null);
  const [selection, setSelection] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [onSaveError, setOnSaveError] = useState();
  const { branding } = useOrgBranding();

  const onConfirm = async () => {
    if (selection) {
      setOnSaveError(null);
      setIsSaving(true);

      try {
        await onUpdate(selection, activeTab);
        onHideModal();

        if (activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES) {
          const unsplashDownloadEvent = `${get(
            selection,
            "links.download_location"
          )}/?client_id=${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`;

          // no need to wait for this to complete
          fetch(unsplashDownloadEvent);
        }
      } catch (mutationError) {
        reportError(mutationError);
        setOnSaveError(copywriting.FORM_SAVE_ERROR);
      }

      setIsSaving(false);

      trackEvent(resourceTrackingType, {
        unsplash_saved: activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        company_images_saved: activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
        color_picker_saved: activeTab === IMAGE_SOURCE_TYPES.COLOR_PICKER,
      });
    } else {
      setOnSaveError(copywriting.FORM_INVALID);
    }
  };

  const onCancel = useCallback(() => {
    const selectedUrl = getImageUrl(selection);
    if (selectedUrl) {
      setSelection(null);
    } else {
      setOnSaveError(null);
      onHideModal();
      trackEvent(resourceTrackingType, {
        cancelled: true,
      });
    }
  }, [selection, onHideModal, resourceTrackingType]);

  const onSelect = useCallback(
    (selected) => {
      setSelection(selected?.data);
      trackEvent(resourceTrackingType, {
        unsplash_selected: activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        company_images_selected:
          activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
        color_picker_selected: activeTab === IMAGE_SOURCE_TYPES.COLOR_PICKER,
      });
    },
    [resourceTrackingType, activeTab]
  );

  const clearSearchTermRef = useCallback(() => {
    searchTermRef.current = "";
  }, []);

  const tabs = useMemo(() => {
    return [
      {
        id: IMAGE_SOURCE_TYPES.COMPANY_IMAGES,
        label: "Company Images",
        isDisabled: false,
      },
      {
        id: IMAGE_SOURCE_TYPES.STOCK_IMAGES,
        label: "Stock Images",
        isDisabled: !!branding?.stockImagesDisabled,
      },
      {
        id: IMAGE_SOURCE_TYPES.COLOR_PICKER,
        label: "Color Picker",
        isDisabled: false,
      },
    ]
      .filter((tab) => allowedSources.includes(tab.id))
      .map((tab) => ({
        ...tab,
        onClick: () => {
          clearSearchTermRef();
          setActiveTab(tab.id);
          setSelection(null);
        },
        className: activeTab === tab.id ? "active" : "",
      }))
      .filter((tab) => !tab.isDisabled);
  }, [branding, allowedSources, activeTab, clearSearchTermRef]);

  useEffect(() => {
    if (!activeTab) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const modalHeader = (
    <>
      <H3 ml="r" mb="s">
        {copywriting.TITLE}
      </H3>
      {tabs.length > 1 && <Tabs tabsList={tabs} />}
    </>
  );

  const modalFooter = (
    <Flex flexWrap="wrap" width="100%" gap="s">
      <Flex gap="s">
        <Button
          onClick={onConfirm}
          variant="primary"
          iconLeft
          disabled={isSaving || !selection}
          isLoading={isSaving}
          data-testid="cp-unsplash-save-btn"
        >
          Apply
        </Button>
        <Button
          variant="ghost"
          onClick={onCancel}
          iconLeft
          data-testid="cp-unsplash-cancel-btn"
        >
          Cancel
        </Button>
      </Flex>
      {onSaveError && (
        <Small variant="danger" fontWeight="bold" display="block">
          <Icon icon={icon.exclamation} mr="xs" />
          {onSaveError}
        </Small>
      )}
      {activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES && (
        <Small variant="secondary" display="block" ml="auto">
          <em>{copywriting.ATTRIBUTION_LABEL}</em>
        </Small>
      )}
    </Flex>
  );

  const leadingItems = useMemo(() => {
    if (defaultBackgroundItem) {
      return [defaultBackgroundItem];
    }
    return [];
  }, [defaultBackgroundItem]);

  const imageUrl = getImageUrl(selection, config?.maxImageWidth);

  return (
    <StyledModal
      visible={isVisible}
      onConfirm={onConfirm}
      onClose={onCancel}
      height={sizing.MODAL_HEIGHT}
      width={sizing.MODAL_WIDTH}
      maxHeight={sizing.MODAL_MAXHEIGHT}
      maxWidth={sizing.MODAL_MAXWIDTH}
      footerContent={modalFooter}
      headerContent={modalHeader}
    >
      <Box p="r" pt="0" height="100%">
        {!imageUrl && (
          <Box>
            {activeTab === IMAGE_SOURCE_TYPES.STOCK_IMAGES && (
              <UnsplashTab
                onSelect={onSelect}
                scrollContainer="window"
                imageWidth={config.maxImageWidth}
                onClickLeadingItems={onSelect}
                leadingItems={leadingItems}
                searchTermRef={searchTermRef}
                avatarMode={layoutMode === "avatar"}
              />
            )}
            {activeTab === IMAGE_SOURCE_TYPES.COMPANY_IMAGES && (
              <CompanyImagesTab
                onSelect={onSelect}
                onClickLeadingItems={onSelect}
                leadingItems={leadingItems}
                avatarMode={layoutMode === "avatar"}
              />
            )}
            {activeTab === IMAGE_SOURCE_TYPES.COLOR_PICKER && (
              <ColorPickerTab
                onSelect={onSelect}
                initialValue={initialValue}
                defaultBackgroundItem={defaultBackgroundItem}
              />
            )}
          </Box>
        )}
        {imageUrl && (
          <ImageCropTool
            layoutMode={layoutMode}
            aspectRatio={config.aspectRatio}
            previewPosition={config.previewPosition}
            imageHeight={config.imageHeight}
            imageUrl={imageUrl}
            croppedAreaPixels={selection?.cropData}
            setCroppedAreaPixels={(cropData, cropArea) => {
              // cropArea is percentage-based, cropData is pixel-based
              const dataToStore =
                config.cropDataField === CROP_DATA_FIELD.CROP_AREA
                  ? cropArea
                  : cropData;
              setSelection((prev) => ({
                ...prev,
                cropData: dataToStore,
              }));
            }}
          />
        )}
      </Box>
    </StyledModal>
  );
};

BannerImagePicker.propTypes = {
  isVisible: PropTypes.bool,
  onUpdate: PropTypes.func,
  onHideModal: PropTypes.func,
  resourceTrackingType: PropTypes.string,
  allowedSources: PropTypes.arrayOf(PropTypes.string),
  initialValue: PropTypes.object,
  defaultBackgroundItem: PropTypes.object,
  layoutMode: PropTypes.oneOf(["avatar", "background"]),
  layoutConfig: PropTypes.shape({
    aspectRatio: PropTypes.number,
    imageHeight: PropTypes.number,
    previewPosition: PropTypes.string,
    maxImageWidth: PropTypes.number,
    cropDataField: PropTypes.string,
  }),
};

export { IMAGE_SOURCE_TYPES };
export default React.memo(BannerImagePicker);
